import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDownloadCsvMutation } from '../../../../hooks/mutations/useDownloadMutation';
import { DataItem } from '../../../../services/apiSLTP/activities';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getMaterialStateColor } from '../../../../utils/colors';
import { formatDate, formatDateToDDMMYYYY, formatDateToYYYYMMDD } from '../../../../utils/dates';
import { exportTypeEnum, getMaterialStateInfos, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import ExportCsvIcon from '../../../ExportCsvIcon';
import GenericAgGridTable from '../../GenericAgGridTable';
import { useAuth } from '../../../../hooks/useAuth';
import { stepModeEnum } from '../../../../constants';

interface ToolsTableProps {
    activities: DataItem[];
    currentDate: Date;
    stepMode: number;
}

const ToolsTable: React.FC<ToolsTableProps> = ({ activities, currentDate, stepMode }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { mutate: exportCsv, isLoading: isExportCsvLoading } = useDownloadCsvMutation();

    const rows = useMemo(() => {
        const dailyTools = activities
            .map((activity) =>
                activity.dailyTools.map((tool) => ({
                    ...tool,
                    activityId: activity._id,
                    siteName: activity.siteId.name,
                    reportUserFirstName: activity.reportId.userId.firstName,
                    reportUserLastName: activity.reportId.userId.lastName,
                }))
            )
            .flat();

        return dailyTools.sort((a, b) => {
            if (a.toolId.designation > b.toolId.designation) return 1;
            if (a.toolId.designation < b.toolId.designation) return -1;
            if (a.state < b.state) return 1;
            if (a.state > b.state) return -1;
            return 0;
        });
    }, [activities]);

    type ToolRow = (typeof rows)[number];

    const columnDefs: ColDef<ToolRow>[] = [
        {
            headerName: capitalizeFirstLetter(t('date')),
            valueGetter: () => formatDateToDDMMYYYY(currentDate, '/'),
            colId: 'date',
            initialHide: true,
        },
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'toolId.designation',
            colId: 'designation',
            initialFlex: 1,
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'toolId.codeProC',
            colId: 'codeProC',
        },
        {
            headerName: capitalizeFirstLetter(t('state')),
            colId: 'state',
            valueGetter: (params) => capitalizeFirstLetter(t(getMaterialStateInfos(params.data?.state))),
            cellRenderer: (params: ICellRendererParams<ToolRow>) => {
                if (!params.data) return params.value;
                return (
                    <StateCell
                        stateColor={getMaterialStateColor(params.data?.state)}
                        text={capitalizeFirstLetter(t(getMaterialStateInfos(params.data?.state)))}
                    />
                );
            },
        },
        {
            headerName: capitalizeFirstLetter(t('site')),
            field: 'siteName',
            colId: 'site',
            initialWidth: 400,
        },
        {
            headerName: capitalizeFirstLetter(t('used')),
            field: 'isUsed',
            colId: 'used',
        },
        {
            headerName: capitalizeFirstLetter(t('reportOf')),
            valueGetter: (params) => formatName(params.data?.reportUserFirstName, params.data?.reportUserLastName),
            colId: 'reportOf',
        },
        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<ToolRow>[]),
    ];

    return (
        <GenericAgGridTable
            rowData={rows}
            columnDefs={columnDefs}
            exportCsvFileName={`supervision-tools-${formatDate(currentDate)}`}
            headerRightComponent={
                <>
                        <ExportCsvIcon
                            initDate={currentDate}
                            onExport={(startDate, endDate, fileName, delimiter) =>
                                exportCsv({
                                    exportType: exportTypeEnum.TOOLS,
                                    queries: { startDate, endDate, delimiter, fileName },
                                })
                            }
                            exportIsLoading={isExportCsvLoading}
                            subTitle={capitalizeFirstLetter(t('tools'))}
                        />
            
                </>
            }
            agGridProps={{ getRowId: ({ data }) => data._id }}
        />
    );
};

export default ToolsTable;
